/**
 * AI pilots (spec §7). Each pilot writes the same `Control` component the player does (D-013): no forces,
 * positions or teleports of its own. Everything a pilot remembers lives in its `AIPilot` component;
 * randomness comes only from the seeded sim RNG (determinism); nothing is allocated per tick (D-014).
 *
 * `AIPilot` field meaning per state:
 * | state  | aimX/Y/Z, aimVX/VY/VZ          | sampleTimer             | jinkX/Y                       | stateTimer             |
 * |--------|--------------------------------|-------------------------|-------------------------------|------------------------|
 * | patrol | current waypoint (world)       | –                       | –                             | waypoint timeout (s)   |
 * | engage | held target position/velocity  | age of the held sample  | aim offset (rad, right/up)    | –                      |
 * | evade  | –                              | time to the next jink   | jink direction (unit, x/y)    | evade time left (s)    |
 * | flee   | –                              | –                       | –                             | –                      |
 * `target` is the nearest living enemy (refreshed every tick) and `lastAttacker` the last ship that hit it.
 *
 * Additive (M3 R19, D-TBD 12): with `AiShips.objectives` set, `target` may be a siege objective (a rebooting
 * relay) weighed at distance / `SiegeObjective.weight`. A pilot besieging an objective skips the anti-joust
 * break (an objective has no nose) and steers around the objective's structure instead of ignoring it (a
 * ship target is left to range holding). Without objectives the behaviour is M1's, bit for bit.
 *
 * Additive (M6 P25, D-403): a **commanded-state overlay** (`setPilotOverlay`; the wing, src/wing). For a pilot the
 * overlay commands, it may replace the perceived target, fly the ship itself for the tick (formation, hold, return;
 * M1's state machine is skipped, avoidance still runs) and scale its effectiveness (aim error and reaction delay are
 * divided by it: the corvette's +10%). Without an overlay, or for a pilot it does not command, the behaviour is M1's,
 * bit for bit.
 *
 * Additive (M10 P4b5d, Q11): an AI-flown L whose traffic pilot recorded its def band (`setPilotBand`) reads
 * `tuning.bands.L`, a stand-off tuning (engage and hold from range, no Evade, so no jinks, no anti-joust break, an
 * avoidance ray scaled to its hull), through a module scratch `AiData` filled per tick (`tuningFor`). Every other
 * pilot reads the global tuning object itself: its steering is M1's, bit for bit.
 */
import { EV, firstReadable, eventSlot } from '../combat/events'
import { createLeadSolution, LeadArgs, solveLeadWith } from '../combat/lead'
import { clamp, type Quat, type Vec3 } from '../core/math'
import { nextFloat, range, type Rng } from '../core/rng'
import { bandRank } from '../data/bands'
import type { AiBandTuning, AiData, AiProfile, ShipDef, WeaponDef, WeaponsData } from '../data/schemas'
import type { RaycastFn } from '../physics/physics'
import { AIPilot, Control, Hull, Life, MAX_ENTITIES, Pos64, Shield, Ship, Vel, Weapons } from '../world/components'
import { applyAvoidance } from './avoidance'
import {
  distanceSq,
  forwardOf,
  isInRearHemisphere,
  isLiveObjective,
  isLiveTarget,
  nearestEnemyOrObjective,
  type AiEventReader,
} from './perception'
import { GaussianOut, gaussianTo, lerp, loadRot, SteerArgs, steerTo, steerToward } from './steering'

/** `AIPilot.state` values (a const object: `erasableSyntaxOnly`). 0 is the spawn state. */
export const AI_STATE = { patrol: 0, engage: 1, evade: 2, flee: 3 } as const
export type AiState = (typeof AI_STATE)[keyof typeof AI_STATE]

/** The ships a pilot perceives and the ones it flies. */
export interface AiShips {
  /** Every live ship entity (targets, attackers). */
  all: ArrayLike<number>
  /** The ships with an `AIPilot` component, stepped in this order. */
  pilots: ArrayLike<number>
  /**
   * Additive (M3 R19): damageable siege objectives (entities with a `SiegeObjective`, not ships), weighed as
   * targets at distance / weight. Absent (M1, and the Reach outside a relay reboot): ships only.
   */
  objectives?: ArrayLike<number>
}

/** The static data pilots read. The objects are the live (dev-panel tunable) runtime data. */
export interface AiDefs {
  /** Indexed by `Ship.defIndex`. */
  ships: readonly ShipDef[]
  weapons: WeaponsData
  /** Patrol waypoints are seeded within `tuning.patrol.radius` of this world point (the spawn). */
  patrolCenter: Vec3
}

/**
 * Additive (M6 P25): a commanded-state overlay read by `stepAIPilots` (the wing). Every method is allocation-free and
 * called per pilot per tick; the others only for a pilot `commands` returns true for.
 */
export interface PilotOverlay {
  /** True when the overlay commands pilot `eid`. */
  commands(eid: number): boolean
  /** The target pilot `eid` perceives this tick, given M1's pick `nearest` (return it to keep M1's choice; −1 = none). */
  target(eid: number, ships: AiShips, nearest: number): number
  /**
   * After perception and the Flee rule: true when the overlay wrote `eid`'s `Control` this tick (M1's state machine
   * is skipped; obstacle avoidance still runs). False hands the tick to M1's states with the overlay's target.
   */
  fly(eid: number, def: ShipDef, now: number, dt: number): boolean
  /** Effectiveness multiplier (above 1 is better): aim error and reaction delay are divided by it. 1 = M1's. */
  effectiveness(eid: number): number
}

const overlayRef: { o: PilotOverlay | null } = { o: null }

/** Installs (or, with null, removes) the commanded-state overlay `stepAIPilots` reads (M6 P25). */
export function setPilotOverlay(o: PilotOverlay | null): void {
  overlayRef.o = o
}

/** The installed overlay, or null. */
export function pilotOverlay(): PilotOverlay | null {
  return overlayRef.o
}

/**
 * The floats of the pilot being stepped (D-014). The state steps are past V8's inlining limit, and a float passed to a
 * call that is not inlined is boxed, one heap number each, per call (as is a non-integer written to a module `let`).
 * A class, not a literal, so its float fields stay unboxed and are written in place (the `SteerArgs` pattern).
 */
class PilotFloats {
  /** The tick length (s). */
  dt = 0
  /** Squared distance to the pilot's target (Infinity = none). */
  targetD2 = 0
  /** The overlay's effectiveness for this pilot (1 = M1's). */
  eff = 1
}

const cur = new PilotFloats()

/** A sample age that always triggers a fresh sample. */
const RESAMPLE = 1e9

// Module scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const right: Vec3 = { x: 0, y: 0, z: 0 }
const up: Vec3 = { x: 0, y: 0, z: 0 }
const fwd: Vec3 = { x: 0, y: 0, z: 0 }
const tgtFwd: Vec3 = { x: 0, y: 0, z: 0 }
const lead = createLeadSolution()
const leadArgs = new LeadArgs()
const normal = new GaussianOut()
const steer = new SteerArgs()

/** `bandRank('L')`: the band index `setPilotBand` records for an L. */
export const PILOT_BAND_L = bandRank('L')

/** The def band rank each pilot's traffic pilot recorded (M10 P4b5d); 0 (S) until set. One index per entity. */
const pilotBand = new Uint8Array(MAX_ENTITIES)

/** Records ship `eid`'s def band rank (`bandRank`) for its pilot; the traffic pilot calls it on promotion. */
export function setPilotBand(eid: number, rank: number): void {
  pilotBand[eid] = rank
}

/** The band rank recorded for `eid` (0 until set). */
export function pilotBandOf(eid: number): number {
  return pilotBand[eid]
}

/** A [0, 0] pair for the scratch tuning (setup only). */
function pair(): [number, number] {
  return [0, 0]
}

/** The scratch `AiData` an L pilot reads (D-014: filled per tick, never allocated). Arrays and sub-objects are the live ones. */
const bandAi: AiData = {
  profiles: [],
  tuning: {
    engageRange: 0,
    holdMin: 0,
    holdMax: 0,
    antiJoustRange: 0,
    antiJoustCone: 0,
    evadeSeconds: pair(),
    evadeThrottle: 0,
    fleeThrottle: 0,
    jinkSeconds: pair(),
    aimError: pair(),
    reactionDelay: pair(),
    fleeHullFactor: 0,
    avoidLookahead: 0,
    avoidMinDistance: 0,
    steering: { turnGain: 0, rollGain: 0, rangeGain: 0, avoidGain: 0 },
    patrol: { radius: 0, arriveDistance: 0, throttle: 0 },
  },
}

/** Fills `bandAi` with the global tuning and band tuning `b` over it for a hull of collider radius `radius`. */
function loadBandAi(ai: AiData, b: AiBandTuning, radius: number): AiData {
  const g = ai.tuning
  const t = bandAi.tuning
  bandAi.profiles = ai.profiles
  t.engageRange = b.engageRange
  t.holdMin = b.holdMin
  t.holdMax = b.holdMax
  t.antiJoustRange = b.antiJoust ? g.antiJoustRange : 0
  t.antiJoustCone = g.antiJoustCone
  t.evadeSeconds = g.evadeSeconds
  t.evadeThrottle = g.evadeThrottle
  t.fleeThrottle = g.fleeThrottle
  t.jinkSeconds = g.jinkSeconds
  t.aimError = g.aimError
  t.reactionDelay = g.reactionDelay
  t.fleeHullFactor = g.fleeHullFactor
  t.avoidLookahead = g.avoidLookahead
  t.avoidMinDistance = b.avoidRadii * radius
  t.steering = g.steering
  t.patrol = g.patrol
  return bandAi
}

/** True when `eid` flies with the L tuning: its recorded band is L and so is its def (a reused entity never inherits it). */
function fliesBandL(ai: AiData, eid: number, def: ShipDef): boolean {
  return ai.tuning.bands !== undefined && pilotBand[eid] === PILOT_BAND_L && def.sizeBand === 'L'
}

/**
 * The AI data pilot `eid` (flying `def`) reads this tick: `ai` itself, or for an L recorded by its traffic pilot, the
 * scratch filled from `tuning.bands.L` (valid until the next call). Allocation-free.
 */
export function tuningFor(ai: AiData, eid: number, def: ShipDef): AiData {
  const bands = ai.tuning.bands
  return bands !== undefined && fliesBandL(ai, eid, def) ? loadBandAi(ai, bands.L, def.colliderRadius) : ai
}

function profileOf(ai: AiData, eid: number): AiProfile {
  const i = AIPilot.profile[eid]
  return ai.profiles[i < ai.profiles.length ? i : ai.profiles.length - 1]
}

/** Seconds between target samples: lerp(reactionDelay[0], reactionDelay[1], skill). */
export function reactionDelayOf(ai: AiData, profile: AiProfile): number {
  return lerp(ai.tuning.reactionDelay[0], ai.tuning.reactionDelay[1], profile.skill)
}

/** Aim-error sigma (rad): lerp(aimError[0], aimError[1], skill). */
export function aimSigmaOf(ai: AiData, profile: AiProfile): number {
  return lerp(ai.tuning.aimError[0], ai.tuning.aimError[1], profile.skill)
}

/** Hull fraction below which a pilot flees: (1 − bravery) × fleeHullFactor (bravery 1 never flees). */
export function fleeThresholdOf(ai: AiData, profile: AiProfile): number {
  return (1 - profile.bravery) * ai.tuning.fleeHullFactor
}

/** The weapon the pilot aims with: the selected primary slot, else the first primary, else the first mount. */
export function aimWeaponOf(def: ShipDef, weapons: WeaponsData, eid: number): WeaponDef {
  const slot = Weapons.primaryIndex[eid]
  let first = -1
  for (let i = 0; i < def.mounts.length; i++) {
    const m = def.mounts[i]
    if (m.group !== 'primary') continue
    if (m.slot === slot) return weapons.weapons[m.weapon]
    if (first < 0) first = i
  }
  return weapons.weapons[def.mounts[first >= 0 ? first : 0].weapon]
}

function clearControl(eid: number): void {
  Control.surge[eid] = 0
  Control.sway[eid] = 0
  Control.heave[eid] = 0
  Control.pitch[eid] = 0
  Control.yaw[eid] = 0
  Control.roll[eid] = 0
  Control.boost[eid] = 0
  Control.firePrimary[eid] = 0
  Control.fireSecondary[eid] = 0
}

export function enterPatrol(eid: number): void {
  AIPilot.state[eid] = AI_STATE.patrol
  AIPilot.stateTimer[eid] = 0 // pick a waypoint on the next step
}

export function enterEngage(eid: number): void {
  AIPilot.state[eid] = AI_STATE.engage
  AIPilot.sampleTimer[eid] = RESAMPLE
}

export function enterEvade(eid: number, ai: AiData, rng: Rng): void {
  AIPilot.state[eid] = AI_STATE.evade
  AIPilot.stateTimer[eid] = range(rng, ai.tuning.evadeSeconds[0], ai.tuning.evadeSeconds[1])
  AIPilot.sampleTimer[eid] = 0 // jink at once
}

export function enterFlee(eid: number): void {
  AIPilot.state[eid] = AI_STATE.flee
}

/**
 * Reads the hits since the pilots' last step: the attacker (`other`) becomes the victim's `lastAttacker`;
 * a hit from the rear hemisphere sends a patrolling or engaged pilot into Evade, any other hit wakes a
 * patrolling pilot into Engage.
 */
function readHits(ships: AiShips, defs: AiDefs, ai: AiData, events: AiEventReader, rng: Rng): void {
  const ring = events.ring
  for (let i = firstReadable(ring, events.cursor); i < ring.head; i++) {
    const s = eventSlot(i)
    const type = ring.type[s]
    if (type !== EV.hitShield && type !== EV.hitHull) continue
    const victim = ring.eid[s]
    let isPilot = false
    for (let p = 0; p < ships.pilots.length; p++) {
      if (ships.pilots[p] === victim) {
        isPilot = true
        break
      }
    }
    if (!isPilot || Life.alive[victim] !== 1) continue
    const attacker = ring.other[s]
    if (attacker >= 0) AIPilot.lastAttacker[victim] = attacker
    const state = AIPilot.state[victim]
    if (state !== AI_STATE.patrol && state !== AI_STATE.engage) continue
    if (
      attacker >= 0 &&
      isLiveTarget(ships, attacker) &&
      isInRearHemisphere(victim, Pos64.x[attacker], Pos64.y[attacker], Pos64.z[attacker]) &&
      evadesOnHit(ai, victim, defs.ships[Ship.defIndex[victim]])
    ) {
      enterEvade(victim, ai, rng)
    } else if (state === AI_STATE.patrol) {
      enterEngage(victim)
    }
  }
  events.cursor = ring.head
}

/** False for an L pilot whose band tuning turns Evade off (M10 P4b5d): a rear hit then only wakes it. */
function evadesOnHit(ai: AiData, eid: number, def: ShipDef): boolean {
  const bands = ai.tuning.bands
  return bands === undefined || bands.L.evade || !fliesBandL(ai, eid, def)
}

/** A new waypoint, uniform in the patrol sphere, and a timeout of twice the travel time at patrol throttle. */
function pickWaypoint(eid: number, def: ShipDef, ai: AiData, center: Vec3, rng: Rng): void {
  const patrol = ai.tuning.patrol
  const u = range(rng, -1, 1)
  const phi = range(rng, 0, Math.PI * 2)
  const r = patrol.radius * Math.cbrt(nextFloat(rng))
  const ring = Math.sqrt(1 - u * u)
  AIPilot.aimX[eid] = center.x + ring * Math.cos(phi) * r
  AIPilot.aimY[eid] = center.y + u * r
  AIPilot.aimZ[eid] = center.z + ring * Math.sin(phi) * r
  AIPilot.aimVX[eid] = AIPilot.aimVY[eid] = AIPilot.aimVZ[eid] = 0
  const d = Math.sqrt(
    (AIPilot.aimX[eid] - Pos64.x[eid]) ** 2 + (AIPilot.aimY[eid] - Pos64.y[eid]) ** 2 + (AIPilot.aimZ[eid] - Pos64.z[eid]) ** 2,
  )
  const cruise = Math.max(patrol.throttle * def.maxSpeed, 1)
  AIPilot.stateTimer[eid] = (2 * d) / cruise + 1
}

/** Returns true when it switched to Engage instead (the caller then runs Engage this tick). */
function stepPatrol(eid: number, def: ShipDef, ai: AiData, defs: AiDefs, rng: Rng): boolean {
  const t = ai.tuning
  const dt = cur.dt
  const targetD2 = cur.targetD2
  if (AIPilot.target[eid] >= 0 && targetD2 <= t.engageRange * t.engageRange) {
    enterEngage(eid)
    return true
  }
  let dx = AIPilot.aimX[eid] - Pos64.x[eid]
  let dy = AIPilot.aimY[eid] - Pos64.y[eid]
  let dz = AIPilot.aimZ[eid] - Pos64.z[eid]
  if (AIPilot.stateTimer[eid] <= 0 || dx * dx + dy * dy + dz * dz < t.patrol.arriveDistance * t.patrol.arriveDistance) {
    pickWaypoint(eid, def, ai, defs.patrolCenter, rng)
    dx = AIPilot.aimX[eid] - Pos64.x[eid]
    dy = AIPilot.aimY[eid] - Pos64.y[eid]
    dz = AIPilot.aimZ[eid] - Pos64.z[eid]
  }
  AIPilot.stateTimer[eid] -= dt
  steerToward(eid, dx, dy, dz, t.steering.turnGain, t.steering.rollGain)
  Control.surge[eid] = t.patrol.throttle
  return false
}

/**
 * `rotateVec(q, vx, vy, vz, out)` for the module scratch `q`, reading the quaternion inside (D-014): `stepEngage` is
 * past V8's inlining limit and runs out of inlining budget before every `rotateVec` → `rotateRaw` call is inlined, so
 * the four quaternion floats it passed to each `rotateRaw` left were boxed, one heap number each, per call. Here only
 * the axis (small integers, never boxed) crosses the call. Same arithmetic as `rotateRaw`, bit for bit.
 */
function rotateAxis(vx: number, vy: number, vz: number, out: Vec3): void {
  const qx = q.x
  const qy = q.y
  const qz = q.z
  const qw = q.w
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  out.x = vx + qw * tx + (qy * tz - qz * ty)
  out.y = vy + qw * ty + (qz * tx - qx * tz)
  out.z = vz + qw * tz + (qx * ty - qy * tx)
}

/** Writes ship `eid`'s right, up and nose axes (world) to `right`, `up` and `fwd` (D-014: no float crosses a call). */
function loadBasis(eid: number): void {
  loadRot(eid, q)
  rotateAxis(1, 0, 0, right)
  rotateAxis(0, 1, 0, up)
  rotateAxis(0, 0, -1, fwd)
}

/**
 * Returns true when it fell back to Patrol instead (no enemy inside engageRange; the caller then runs Patrol).
 * `siege` (M3 R19): the target is a siege objective, which has no nose to joust with.
 */
function stepEngage(eid: number, def: ShipDef, ai: AiData, defs: AiDefs, rng: Rng, siege: boolean): boolean {
  const t = ai.tuning
  const dt = cur.dt
  const targetD2 = cur.targetD2
  const curEff = cur.eff
  const target = AIPilot.target[eid]
  if (target < 0 || targetD2 > t.engageRange * t.engageRange) {
    enterPatrol(eid)
    return true
  }
  const profile = profileOf(ai, eid)
  const sigma = aimSigmaOf(ai, profile) / curEff

  // Reaction delay: sample and hold the target's state; aim with the held sample extrapolated by its age.
  let age = AIPilot.sampleTimer[eid] + dt
  if (age >= reactionDelayOf(ai, profile) / curEff) {
    AIPilot.aimX[eid] = Pos64.x[target]
    AIPilot.aimY[eid] = Pos64.y[target]
    AIPilot.aimZ[eid] = Pos64.z[target]
    AIPilot.aimVX[eid] = Vel.x[target]
    AIPilot.aimVY[eid] = Vel.y[target]
    AIPilot.aimVZ[eid] = Vel.z[target]
    gaussianTo(rng, normal)
    AIPilot.jinkX[eid] = sigma * normal.value
    gaussianTo(rng, normal)
    AIPilot.jinkY[eid] = sigma * normal.value
    age = 0
  }
  AIPilot.sampleTimer[eid] = age
  const vx = AIPilot.aimVX[eid]
  const vy = AIPilot.aimVY[eid]
  const vz = AIPilot.aimVZ[eid]
  const px = AIPilot.aimX[eid] + vx * age
  const py = AIPilot.aimY[eid] + vy * age
  const pz = AIPilot.aimZ[eid] + vz * age

  const mx = Pos64.x[eid]
  const my = Pos64.y[eid]
  const mz = Pos64.z[eid]
  const weapon = aimWeaponOf(def, defs.weapons, eid)
  const reach = weapon.kind === 'projectile' ? weapon.range : weapon.lock.range
  const la = leadArgs
  la.sx = mx
  la.sy = my
  la.sz = mz
  la.svx = Vel.x[eid]
  la.svy = Vel.y[eid]
  la.svz = Vel.z[eid]
  la.tx = px
  la.ty = py
  la.tz = pz
  la.tvx = vx
  la.tvy = vy
  la.tvz = vz
  la.speed = weapon.speed
  solveLeadWith(la, lead)

  // The pilot's aim error: the held offset, in radians across the line of sight (right, up).
  loadBasis(eid)
  let ax = lead.x - mx
  let ay = lead.y - my
  let az = lead.z - mz
  const aimDist = Math.sqrt(ax * ax + ay * ay + az * az)
  const ox = AIPilot.jinkX[eid] * aimDist
  const oy = AIPilot.jinkY[eid] * aimDist
  ax += right.x * ox + up.x * oy
  ay += right.y * ox + up.y * oy
  az += right.z * ox + up.z * oy
  const sa = steer
  sa.dx = ax
  sa.dy = ay
  sa.dz = az
  sa.turnGain = t.steering.turnGain
  sa.rollGain = t.steering.rollGain
  steerTo(eid, sa)
  const aimError = sa.angle

  // Hold holdMin..holdMax behind the target: match its speed along the nose, correct the range error.
  const hx = px - mx
  const hy = py - my
  const hz = pz - mz
  const heldDist = Math.sqrt(hx * hx + hy * hy + hz * hz)
  const rangeError = heldDist > t.holdMax ? heldDist - t.holdMax : heldDist < t.holdMin ? heldDist - t.holdMin : 0
  const along = (vx * fwd.x + vy * fwd.y + vz * fwd.z) / def.maxSpeed
  Control.surge[eid] = clamp(along + t.steering.rangeGain * rangeError, -1, 1)
  // Out of weapon reach: close the gap at boost.
  Control.boost[eid] = heldDist > reach ? 1 : 0
  Control.firePrimary[eid] = aimError < defs.weapons.gimbal + sigma && heldDist <= reach ? 1 : 0

  // Anti-joust: nose to nose inside antiJoustRange → break off instead of flying through.
  const d2 = targetD2
  if (!siege && d2 < t.antiJoustRange * t.antiJoustRange && d2 > 0) {
    const d = Math.sqrt(d2)
    const tx = (Pos64.x[target] - mx) / d
    const ty = (Pos64.y[target] - my) / d
    const tz = (Pos64.z[target] - mz) / d
    const cone = Math.cos(t.antiJoustCone)
    forwardOf(target, tgtFwd)
    if (fwd.x * tx + fwd.y * ty + fwd.z * tz > cone && -(tgtFwd.x * tx + tgtFwd.y * ty + tgtFwd.z * tz) > cone) {
      Control.firePrimary[eid] = 0
      enterEvade(eid, ai, rng)
    }
  }
  return false
}

function stepEvade(eid: number, ai: AiData, rng: Rng): void {
  const t = ai.tuning
  const dt = cur.dt
  AIPilot.sampleTimer[eid] -= dt
  if (AIPilot.sampleTimer[eid] <= 0) {
    // A full-deflection jink in a random direction across the nose.
    const a = range(rng, 0, Math.PI * 2)
    AIPilot.jinkX[eid] = Math.cos(a)
    AIPilot.jinkY[eid] = Math.sin(a)
    AIPilot.sampleTimer[eid] = range(rng, t.jinkSeconds[0], t.jinkSeconds[1])
  }
  const jx = AIPilot.jinkX[eid]
  const jy = AIPilot.jinkY[eid]
  Control.surge[eid] = t.evadeThrottle
  Control.sway[eid] = jx
  Control.heave[eid] = jy
  Control.yaw[eid] = jx
  Control.pitch[eid] = jy
  AIPilot.stateTimer[eid] -= dt
  if (AIPilot.stateTimer[eid] <= 0) enterEngage(eid)
}

function stepFlee(eid: number, def: ShipDef, ships: AiShips, ai: AiData): void {
  const attacker = isLiveTarget(ships, AIPilot.lastAttacker[eid]) ? AIPilot.lastAttacker[eid] : AIPilot.target[eid]
  if (attacker >= 0) {
    steerToward(
      eid,
      Pos64.x[eid] - Pos64.x[attacker],
      Pos64.y[eid] - Pos64.y[attacker],
      Pos64.z[eid] - Pos64.z[attacker],
      ai.tuning.steering.turnGain,
      ai.tuning.steering.rollGain,
    )
  }
  Control.surge[eid] = ai.tuning.fleeThrottle
  if (Shield.value[eid] >= def.shield) enterEngage(eid)
}

/**
 * One AI tick (spec §4.2 step 1) for every pilot in `ships.pilots`: perception (nearest living enemy, new
 * hits), the Flee rule, the state machine, then obstacle avoidance. Writes only `Control` and `AIPilot`.
 *
 * Flee rule: a pilot flees while its hull fraction is below `(1 − bravery) × fleeHullFactor` and it is under
 * fire (hit within its ship's `shieldRegenDelay`); it re-engages once its shields are full again, which
 * cannot happen until the shooting stops, so a wounded pilot runs, recovers and returns until hit again.
 */
export function stepAIPilots(
  ships: AiShips,
  defs: AiDefs,
  ai: AiData,
  raycast: RaycastFn,
  events: AiEventReader,
  rng: Rng,
  now: number,
  dt: number,
): void {
  readHits(ships, defs, ai, events, rng)
  const overlay = overlayRef.o
  cur.dt = dt
  for (let i = 0; i < ships.pilots.length; i++) {
    const eid = ships.pilots[i]
    clearControl(eid)
    if (Life.alive[eid] !== 1) continue
    const def = defs.ships[Ship.defIndex[eid]]
    // The global tuning, or an L's stand-off tuning (M10 P4b5d); `ai` itself for every S/M pilot.
    const pai = tuningFor(ai, eid, def)
    const commanded = overlay !== null && overlay.commands(eid)
    cur.eff = commanded ? overlay.effectiveness(eid) : 1

    // Perception: the nearest living enemy (or a siege objective that weighs less) is the target; a new
    // target means fresh aim data. A commanded pilot's target comes from the overlay (M6 P25).
    const nearest = nearestEnemyOrObjective(ships, eid)
    const target = commanded ? overlay.target(eid, ships, nearest) : nearest
    if (target !== AIPilot.target[eid]) {
      AIPilot.target[eid] = target
      if (AIPilot.state[eid] === AI_STATE.engage) AIPilot.sampleTimer[eid] = RESAMPLE
    }
    // Two stores, not a ternary (D-014): a phi of the float and the Infinity constant is tagged, so it boxed the float.
    if (target >= 0) cur.targetD2 = distanceSq(eid, target)
    else cur.targetD2 = Infinity
    const siege = isLiveObjective(ships.objectives, target)

    if (
      AIPilot.state[eid] !== AI_STATE.flee &&
      Hull.value[eid] / def.hull < fleeThresholdOf(pai, profileOf(pai, eid)) &&
      now - Shield.lastHit[eid] < def.shieldRegenDelay
    ) {
      enterFlee(eid)
    }

    if (commanded && overlay.fly(eid, def, now, dt)) {
      applyAvoidance(eid, raycast, pai.tuning, siege ? -1 : target, def.colliderRadius, Math.round(now / dt) + eid)
      continue
    }

    switch (AIPilot.state[eid]) {
      case AI_STATE.engage:
        if (stepEngage(eid, def, pai, defs, rng, siege)) stepPatrol(eid, def, pai, defs, rng)
        break
      case AI_STATE.evade:
        stepEvade(eid, pai, rng)
        break
      case AI_STATE.flee:
        stepFlee(eid, def, ships, pai)
        break
      default:
        if (stepPatrol(eid, def, pai, defs, rng)) stepEngage(eid, def, pai, defs, rng, siege)
    }
    // The first feeler side rotates with the tick and the entity: deterministic. A siege objective's
    // colliders are its structure (the relay ring), which the pilot must steer around, so they are not ignored.
    applyAvoidance(eid, raycast, pai.tuning, siege ? -1 : target, def.colliderRadius, Math.round(now / dt) + eid)
  }
}
